import "./bootstrapImportMeta";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import "./i18n";
import App from "./App";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { initAdjust } from "@/lib/adjust";

Sentry.init({
  dsn: "https://ea988c23c6597b632a002a69e96f9af7@o4510261856894976.ingest.de.sentry.io/4510324855078993",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
});

// Sentry test utility - triggers Sentry errors for testing purposes
// Only activates when explicitly invoked via URL parameter or console command
function setupSentryTestUtils() {
  // Test function that triggers different types of Sentry events
  const testSentry = () => {
    try {
      // Test 1: Capture a message
      Sentry.captureMessage("Sentry Test: Manual test message", "info");

      // Test 2: Capture an exception
      Sentry.captureException(
        new Error("Sentry Test: Test exception for error tracking verification")
      );

      // Test 3: Capture with additional context
      Sentry.captureException(
        new Error("Sentry Test: Test exception with context"),
        {
          tags: { testType: "sentry-verification" },
          extra: {
            timestamp: new Date().toISOString(),
            environment: import.meta.env.MODE,
          },
        }
      );

      return true;
    } catch (error) {
      console.error("❌ Failed to send Sentry test events:", error);
      return false;
    }
  };

  // Expose test function to window for console access
  (window as any).testSentry = testSentry;

  // Check for URL parameter to auto-trigger test
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("test-sentry") === "true") {
    // Small delay to ensure Sentry is fully initialized
    setTimeout(() => {
      testSentry();
      // Remove the parameter from URL without reloading
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("test-sentry");
      window.history.replaceState({}, "", newUrl.toString());
    }, 100);
  }
}

// Initialize Sentry test utilities
setupSentryTestUtils();

// Initialize Adjust SDK
initAdjust();

// Development utilities for testing authentication
if (import.meta.env.DEV) {
  import("./lib/api").then(({ authTestUtils, authApi }) => {
    // Add test utilities to window for easy access in browser console
    (window as any).authTest = authTestUtils;
    (window as any).authApi = authApi;
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <ThemeProvider>
          <AuthProvider>
            <ChatProvider>
              <App />
            </ChatProvider>
          </AuthProvider>
        </ThemeProvider>
      </ToastProvider>
    </BrowserRouter>
  </StrictMode>
);
